import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, Ship, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SeaCargoArrivalNoticePrint from './SeaCargoArrivalNoticePrint';

const DOC_TYPE = 'SEA_CARGO_ARRIVAL_NOTICE';

const EMPTY_FORM = {
  job_no: '',
  date: '',
  shipper: '',
  consignee: '',
  notify: '',
  shipping_line: '',
  vessel_voyage: '',
  vessel_date: '',
  port_of_load: '',
  port_of_discharge: '',
  mbl_no: '',
  mbl_date: '',
  container_no: '',
  size: '',
  hbl_no: '',
  hbl_date: '',
  no_of_packages_hbl: '',
  gross_weight_hbl: '',
  cbm: '',
  description: '',
  igm_no: '',
  igm_date: '',
  temperature: '',
};

// ── helper: build a party address block from resolved party data ──────────────
const buildPartyText = (p) => {
  if (!p) return '';
  return [p.name, p.branch_name, p.address,
    p.phone ? `Tel: ${p.phone}` : '',
    p.email ? `Email: ${p.email}` : '']
    .filter(Boolean).join('\n');
};

const SeaCargoArrivalNoticeForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [docketInfo, setDocketInfo] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (!docketId) return;
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch docket print-data (resolved names, parties, shipment info)
        const docketRes = await fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, { headers: getAuthHeaders() });
        if (!docketRes.ok) throw new Error('Failed to fetch docket data');
        const docketData = await docketRes.json();
        setDocketInfo(docketData);
        if (docketData.company) setCompanyData(docketData.company);

        // Check if SEA CAN already exists for this docket
        const docRes = await fetch(`${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`, { headers: getAuthHeaders() });
        if (docRes.ok) {
          const docData = await docRes.json();
          if (docData?.id) {
            setFormData(docData.document_data);
            setDocumentId(docData.id);
            setIsSaved(true);
            return;
          }
        }

        // No existing record — populate from SEA docket
        populateFromDocket(docketData);
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [docketId]);

  // ── Auto-populate from SEA docket data ─────────────────────────────────────
  const populateFromDocket = (docketData) => {
    const { docket, resolved_parties, resolved_shipment } = docketData;
    const payload = docket.payload_json || {};
    const shipmentInfo = payload.shipment_info || {};
    const header = payload.header || {};
    const invoices = payload.invoices || [];
    const invoice = invoices[0] || {};

    setFormData({
      job_no: docket.system_docket_no || '',
      date: new Date().toISOString().split('T')[0],
      shipper: buildPartyText(resolved_parties?.exporter_shipper),
      consignee: buildPartyText(resolved_parties?.importer_receiver),
      notify: buildPartyText(resolved_parties?.notify_party),
      // AIR → SEA field mapping
      shipping_line: resolved_shipment?.carrier || '',          // Airline → Shipping Line
      vessel_voyage: shipmentInfo.flight_no || '',              // Flight No → Vessel/Voyage
      vessel_date: shipmentInfo.flight_date || '',              // Flight Date → Vessel Date
      port_of_load: resolved_shipment?.loading_from || '',      // Airport of Load → Port of Load
      port_of_discharge: resolved_shipment?.discharge_at || '', // Airport of Discharge → Port of Discharge
      mbl_no: shipmentInfo.mawb_no || '',                       // MAWB No → MBL No
      mbl_date: shipmentInfo.mawb_date || '',                   // MAWB Date → MBL Date
      container_no: '',                                          // SEA-only (no docket field yet)
      size: '',                                                  // SEA-only (no docket field yet)
      hbl_no: shipmentInfo.hawb_no || '',                       // HAWB No → HBL No
      hbl_date: shipmentInfo.hawb_date || '',                   // HAWB Date → HBL Date
      no_of_packages_hbl: invoice.no_of_packages || '',         // No. of Packages → HBL Packages
      gross_weight_hbl: invoice.gross_weight || '',             // Gross Weight → HBL Gross Weight
      cbm: invoice.cbm_chargeable_weight || '',                 // CBM from invoice
      description: invoice.description || shipmentInfo.hawb_description || '',
      igm_no: shipmentInfo.igm_no || '',
      igm_date: shipmentInfo.igm_date || '',
      temperature: header.temp || header.temperature || '',
    });
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const url = isSaved
        ? `${backendUrl}/api/dockets/${docketId}/documents/${DOC_TYPE}`
        : `${backendUrl}/api/dockets/${docketId}/documents`;
      const method = isSaved ? 'PUT' : 'POST';
      const body = isSaved
        ? { document_data: formData }
        : { document_type: DOC_TYPE, document_data: formData };

      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      const saved = await res.json();
      setDocumentId(saved.id);
      setIsSaved(true);
      toast({ title: 'Success', description: 'SEA Cargo Arrival Notice saved successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const origTitle = document.title;
    document.title = `SEA_CAN_${formData.job_no || docketId}`;
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => { document.title = origTitle; }, 1000);
    }, 300);
  };

  // ── Print view ──────────────────────────────────────────────────────────────
  if (showPrintView) {
    document.title = `SEA_CAN_${formData.job_no || docketId}`;
    return (
      <div className="min-h-screen bg-white">
        <div className="print:hidden fixed top-4 left-4 right-4 z-50 flex justify-between">
          <Button variant="outline" onClick={() => { document.title = 'LogisticsHub'; setShowPrintView(false); }} className="bg-white shadow-md">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Form
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="bg-white shadow-md">
              <Download className="w-4 h-4 mr-2" />Save as PDF
            </Button>
            <Button onClick={() => window.print()} className="bg-teal-600 hover:bg-teal-700 shadow-md">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
          </div>
        </div>
        <div className="pt-16">
          <SeaCargoArrivalNoticePrint ref={printRef} docketId={docketId} formData={formData} companyData={companyData} />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // ── Field helper ────────────────────────────────────────────────────────────
  const F = ({ label, field, type = 'text', testId, placeholder = '' }) => (
    <div>
      <Label className="text-xs font-semibold text-text-secondary font-ibm">{label}</Label>
      <Input
        type={type}
        value={formData[field] || ''}
        onChange={e => handleChange(field, e.target.value)}
        className="mt-1"
        data-testid={testId || `sea-can-${field}`}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <Ship className="w-5 h-5 text-teal-600" />
                SEA Cargo Arrival Notice
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Docket: {docketInfo?.docket?.system_docket_no || docketId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSaved && (
              <>
                <Button variant="outline" onClick={handlePrint}>
                  <Download className="w-4 h-4 mr-2" />Save PDF
                </Button>
                <Button variant="outline" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2" />Print
                </Button>
              </>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Form Card */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">SEA CARGO ARRIVAL NOTICE</h2>
          </div>

          <div className="p-5 space-y-5">
            {/* Row 1: Job No, Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Job No.</Label>
                <Input value={formData.job_no} readOnly className="mt-1 bg-gray-50 cursor-not-allowed" data-testid="sea-can-job-no" />
              </div>
              <F label="Date" field="date" type="date" testId="sea-can-date" />
            </div>

            {/* Row 2: Shipper, Consignee, Notify */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Shipper', field: 'shipper', placeholder: 'Shipper name and address' },
                { label: 'Consignee', field: 'consignee', placeholder: 'Consignee name and address' },
                { label: 'Notify Party', field: 'notify', placeholder: 'Notify party' },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">{label}</Label>
                  <Textarea
                    value={formData[field] || ''}
                    onChange={e => handleChange(field, e.target.value)}
                    className="mt-1 h-24"
                    placeholder={placeholder}
                    data-testid={`sea-can-${field}`}
                  />
                </div>
              ))}
            </div>

            {/* Row 3: Shipping Line, Vessel/Voyage, Vessel Date */}
            <div className="grid grid-cols-3 gap-4">
              <F label="Shipping Line" field="shipping_line" testId="sea-can-shipping-line" />
              <F label="Vessel / Voyage" field="vessel_voyage" testId="sea-can-vessel-voyage" />
              <F label="Vessel Date" field="vessel_date" type="date" testId="sea-can-vessel-date" />
            </div>

            {/* Row 4: Port of Load, Port of Discharge */}
            <div className="grid grid-cols-2 gap-4">
              <F label="Port of Load" field="port_of_load" testId="sea-can-port-load" />
              <F label="Port of Discharge" field="port_of_discharge" testId="sea-can-port-discharge" />
            </div>

            {/* Row 5: MBL No, MBL Date */}
            <div className="grid grid-cols-2 gap-4">
              <F label="MBL No." field="mbl_no" testId="sea-can-mbl-no" />
              <F label="MBL Date" field="mbl_date" type="date" testId="sea-can-mbl-date" />
            </div>

            {/* Row 6: Container No, Size */}
            <div className="grid grid-cols-2 gap-4">
              <F label="Container No." field="container_no" testId="sea-can-container-no" placeholder="e.g. ABCU1234567" />
              <F label="Size" field="size" testId="sea-can-size" placeholder="e.g. 20GP, 40HC" />
            </div>

            {/* Row 7: HBL No, HBL Date, No. of Packages HBL, Gross Weight HBL, CBM */}
            <div className="grid grid-cols-5 gap-4">
              <F label="HBL No." field="hbl_no" testId="sea-can-hbl-no" />
              <F label="HBL Date" field="hbl_date" type="date" testId="sea-can-hbl-date" />
              <F label="No. of Packages HBL" field="no_of_packages_hbl" testId="sea-can-packages" />
              <F label="Gross Weight HBL" field="gross_weight_hbl" testId="sea-can-gross-weight" />
              <F label="CBM" field="cbm" testId="sea-can-cbm" />
            </div>

            {/* Row 8: Description */}
            <div>
              <Label className="text-xs font-semibold text-text-secondary font-ibm">Description</Label>
              <Textarea
                value={formData.description || ''}
                onChange={e => handleChange('description', e.target.value)}
                className="mt-1 h-20"
                placeholder="Cargo description"
                data-testid="sea-can-description"
              />
            </div>

            {/* Row 9: IGM No, IGM Date, Temperature */}
            <div className="grid grid-cols-3 gap-4">
              <F label="IGM No." field="igm_no" testId="sea-can-igm-no" />
              <F label="IGM Date" field="igm_date" type="date" testId="sea-can-igm-date" />
              <F label="Temperature" field="temperature" testId="sea-can-temperature" placeholder="e.g. 2-8°C" />
            </div>
          </div>
        </Card>

        {/* Saved status bar */}
        {isSaved && (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-teal-700 font-ibm">
              <span className="font-semibold">Saved</span> — SEA Cargo Arrival Notice linked to docket {docketInfo?.docket?.system_docket_no}
            </p>
            <span className="text-xs text-teal-600">Document ID: {documentId?.slice(0, 8)}...</span>
          </div>
        )}

      </div>
    </div>
  );
};

export default SeaCargoArrivalNoticeForm;
